import { useState } from 'react';
import { t } from '@aquabox/shared';
import {
  useRegionSuggestions,
  type RegionSuggestion,
  type SuggestionChoice,
} from '../api/region-suggestions';

/**
 * "Verilerden bölge öner" paneli: dört kadran taslağı (güven %, mahalleler, sınır mahalleleri,
 * renk noktası, araç). Araç ön-dolu gelir ve değiştirilebilir (yalnız aktif araçlar); uyarılar
 * (eşleme yok / iki bölge aynı araç) bu panelde hesaplanır. Uygulama kararı üstte: seçili
 * taslaklar seçili araç ve renkle `onApply`'a verilir; onay modalı ve yazma `Regions.tsx`'tedir.
 * "Uygula" yalnız OWNER'a görünür; sunucu da yalnız OWNER'a izin verir.
 */
export function RegionSuggestions({
  isOwner,
  onClose,
  onApply,
  busy,
  vehicles,
}: {
  isOwner: boolean;
  onClose: () => void;
  onApply: (choices: SuggestionChoice[]) => void;
  busy: boolean;
  /** Yalnız aktif araçlar (Regions.tsx süzer). */
  vehicles: ReadonlyArray<{ id: string; plate: string }>;
}) {
  const query = useRegionSuggestions(true);
  const [picked, setPicked] = useState<ReadonlySet<string>>(new Set());
  /** Kullanıcının değiştirdiği araçlar (taslak adı → araç kimliği ya da null = araç yok). */
  const [edits, setEdits] = useState<Readonly<Record<string, string | null>>>({});
  const drafts = query.data?.drafts ?? [];

  function vehicleOf(d: RegionSuggestion): string | null {
    return d.name in edits ? (edits[d.name] ?? null) : d.suggestedVehicleId;
  }

  // Uyarılar: seçili araç yoksa eşleme yok; aynı araca iki taslak düşüyorsa ikisi de uyarılı.
  const perVehicle = new Map<string, number>();
  for (const d of drafts) {
    const v = vehicleOf(d);
    if (v) perVehicle.set(v, (perVehicle.get(v) ?? 0) + 1);
  }
  function warningOf(d: RegionSuggestion): 'NO_MAPPING' | 'AMBIGUOUS' | null {
    const v = vehicleOf(d);
    if (v === null) return 'NO_MAPPING';
    return (perVehicle.get(v) ?? 0) > 1 ? 'AMBIGUOUS' : null;
  }

  function toggle(name: string): void {
    setPicked((cur) => {
      const next = new Set(cur);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  }

  function setVehicle(name: string, vehicleId: string): void {
    setEdits((cur) => ({ ...cur, [name]: vehicleId === '' ? null : vehicleId }));
  }

  return (
    <section
      className="card"
      data-testid="region-suggestions"
      style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}
    >
      <header style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <strong style={{ flex: 1, fontSize: 15 }}>{t('region.suggest.title')}</strong>
        <button type="button" className="btn" onClick={onClose}>
          {t('region.suggest.close')}
        </button>
      </header>
      <p style={{ margin: 0, fontSize: 13.5, color: 'var(--text-secondary)' }}>
        {t('region.suggest.intro')}
      </p>

      {query.isLoading ? <p style={{ margin: 0 }}>{t('region.suggest.loading')}</p> : null}
      {query.isError ? (
        <p role="alert" style={{ margin: 0, color: 'var(--danger)' }}>
          {t('region.suggest.loadError')}
        </p>
      ) : null}
      {query.isSuccess && drafts.length === 0 ? (
        <p data-testid="region-suggestions-empty" style={{ margin: 0 }}>
          {t('region.suggest.empty')}
        </p>
      ) : null}

      <div
        style={{
          display: 'grid',
          gap: 10,
          gridTemplateColumns: 'repeat(auto-fill,minmax(260px,1fr))',
        }}
      >
        {drafts.map((d) => {
          const warning = warningOf(d);
          const current = vehicleOf(d);
          return (
            <article
              key={d.name}
              className="card"
              data-testid="region-suggestion-card"
              style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 6 }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {isOwner ? (
                  <input
                    type="checkbox"
                    aria-label={t('region.suggest.select', { name: d.name })}
                    checked={picked.has(d.name)}
                    onChange={() => toggle(d.name)}
                  />
                ) : null}
                <span
                  aria-hidden="true"
                  data-testid="region-suggestion-color"
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    background: d.color,
                    flex: 'none',
                  }}
                />
                <strong style={{ flex: 1 }}>{d.name}</strong>
                <span data-testid="region-suggestion-confidence" style={{ fontSize: 13 }}>
                  {t('region.suggest.confidence', { pct: d.confidence })}
                </span>
              </div>
              <span style={{ fontSize: 13 }}>
                {t('region.suggest.orders', { count: d.orderCount })}
              </span>
              <span style={{ fontSize: 13 }}>
                {t('region.neighborhoods')}: {d.neighborhoods.join(', ')}
              </span>
              {d.border.length > 0 ? (
                <span data-testid="region-suggestion-border" style={{ fontSize: 12.5 }}>
                  {t('region.suggest.border')}: {d.border.join(', ')}
                </span>
              ) : null}
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                {t('region.suggest.vehicleLabel')}
                <select
                  aria-label={t('region.suggest.vehicleLabel')}
                  className="input"
                  data-testid="region-suggestion-vehicle"
                  value={current ?? ''}
                  disabled={!isOwner}
                  onChange={(e) => setVehicle(d.name, e.target.value)}
                >
                  <option value="">{t('region.suggest.vehicleNone')}</option>
                  {vehicles.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.plate}
                    </option>
                  ))}
                </select>
              </label>
              {warning ? (
                <span
                  data-testid="region-suggestion-warning"
                  style={{ fontSize: 12.5, color: 'var(--danger)' }}
                >
                  {warning === 'NO_MAPPING'
                    ? t('region.suggest.vehicleNoMapping')
                    : t('region.suggest.vehicleAmbiguous')}
                </span>
              ) : null}
              <span style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>
                {d.description}
              </span>
            </article>
          );
        })}
      </div>

      {!isOwner && drafts.length > 0 ? (
        <p data-testid="region-suggestions-owner-only" style={{ margin: 0, fontSize: 13 }}>
          {t('region.suggest.ownerOnly')}
        </p>
      ) : null}

      {isOwner && drafts.length > 0 ? (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button
            type="button"
            className="btn btn-primary"
            disabled={busy || picked.size === 0}
            data-testid="region-suggestions-apply"
            onClick={() =>
              onApply(
                drafts
                  .filter((d) => picked.has(d.name))
                  .map((d) => ({
                    name: d.name,
                    neighborhoods: d.neighborhoods,
                    defaultVehicleId: vehicleOf(d),
                    color: d.color,
                  })),
              )
            }
          >
            {t('region.suggest.applySelected')}
          </button>
          {picked.size === 0 ? (
            <span style={{ fontSize: 13 }}>{t('region.suggest.noneSelected')}</span>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
